// src/lib/direct-preview-scenario.js — PARTIE B / B5 (ADR-272) : MODÈLE de
// scénario d'aperçu Direct + DÉRIVATION des drapeaux {#if …}.
//
// EXIGENCE PRODUIT (Dimitri) : sur le même écran que l'éditeur Direct, des cases
// à cocher simulent l'état d'une réservation et transforment l'aperçu EN DIRECT.
// Ce module porte le MODÈLE (état du scénario), le DESCRIPTEUR des contrôles
// (l'UI est data-driven → testable sans DOM) et la DÉRIVATION des drapeaux.
//
// ══════════════════════════════════════════════════════════════════════════════
// ⚠️ RÈGLE SACRÉE — « la caution dit toujours la vérité » (chemin argent)
// ══════════════════════════════════════════════════════════════════════════════
// AUCUNE case à cocher ne pose DIRECTEMENT un drapeau caution. Les drapeaux sont
// DÉRIVÉS, exactement comme côté serveur (lib/direct-vars-catalog.js
// deriveDirectConditionFlags) :
//
//   • caution_empreinte / caution_debit  ← cautionModeForNights(nbNuits)
//     (seuil unique CAUTION_HOLD_MAX_NIGHTS, ADR-174). Le contrôle exposé à
//     l'admin est donc un CHOIX DE DURÉE DE SÉJOUR (« court ≤ N nuits » /
//     « long > N nuits »), JAMAIS deux cases « empreinte »/« débit »
//     indépendantes — sinon le seuil serait contournable dans l'aperçu et
//     Dimitri validerait un texte « empreinte, jamais débitée » sur un séjour
//     long (litige). Le libellé AFFICHE N lu depuis la constante — jamais « 4 »
//     en dur.
//   • caution_recue / caution_manquante / caution_deposee ← cautionRecue()
//     sur les 9 statuts CAUTION_RECUE_STATUSES. Le contrôle est un CHOIX DE
//     STATUT Stripe représentatif, jamais une case libre.
//
// ⚠️ DUPLICATION CONTRÔLÉE (ADR-021) : `deriveDirectPreviewFlags` est le miroir
// EXACT de `deriveDirectConditionFlags` (lib/direct-vars-catalog.js) — même
// signature (vars, signals), même logique, même fail-doux. Toute évolution du
// serveur DOIT être répliquée ici. Parité verrouillée par
// src/lib/direct-preview-scenario.test.js (BLOQUANT CI).
//
// ⚠️ ZÉRO ÉCRITURE : module PUR (aucun réseau, aucun accès base, aucun envoi).
//
// ⚠️ COHÉRENCE ARITHMÉTIQUE DU JEU D'EXEMPLE (QA #1095) : le nombre de nuits est
// PILOTÉ par le panneau (choix de durée) alors que les dates d'exemple viennent
// du serveur (JOURNEY_PREVIEW_VARS : 12/07/2026 → 19/07/2026, soit 7 nuits). Sans
// réalignement, un gabarit « du {from} au {to} — {nbNuits} nuits » afficherait
// « du 12/07/2026 au 19/07/2026 — 4 nuits » : une combinaison qu'AUCUN envoi réel
// ne peut produire. L'ARRIVÉE reste l'ancre (toutes les autres dates d'exemple en
// dépendent : date_caution, date_veille_arrivee, date_limite_paiement) ; seul le
// DÉPART est recalculé = arrivée + nbNuits. Idem pour le montant du séjour, qui
// est un champ du panneau. Voir `realignExampleStrings` + le test bloquant
// « aperçu arithmétiquement possible » (src/lib/direct-preview-scenario.test.js).

import { CAUTION_HOLD_MAX_NIGHTS, cautionModeForNights } from "./caution-mode";
import { cautionRecue } from "./payment-derivation";
// Trichotomie #21 (liberation_totale / prelevement_partiel / prelevement_total).
// On RÉUTILISE le jumeau ESM déjà en place (src/lib/caution-restitution-preview.js,
// miroir de deriveCautionRestitutionFlags de lib/direct-vars-catalog.js) — un 2e
// portage serait une duplication de plus sur le CHEMIN ARGENT.
import { deriveCautionRestitutionFlags } from "./caution-restitution-preview";

function _isNonEmpty(v) {
  return v != null && String(v).trim() !== "";
}

// ── Jeu d'exemple serveur : MIROIR des valeurs de JOURNEY_PREVIEW_VARS ────────
// (lib/journey-templates.js). On ne peut pas importer la racine lib/ depuis src/
// (ModuleScopePlugin CRA) → miroir explicite, VERROUILLÉ par un test qui compare
// ces constantes au jeu d'exemple serveur réel : toute dérive CASSE la CI.
export const PREVIEW_EXAMPLE_ARRIVEE_ISO = "2026-07-12";
export const PREVIEW_EXAMPLE_ARRIVEE_FR = "12/07/2026";
export const PREVIEW_EXAMPLE_DEPART_FR = "19/07/2026";
export const PREVIEW_EXAMPLE_MONTANT_SEJOUR = 840;

function _pad2(n) {
  return String(n).padStart(2, "0");
}

// '2026-07-12' + n jours → 'JJ/MM/AAAA'. UTC pur → aucun effet fuseau/DST, aucun
// Date.now() : déterministe (même sortie en test, en CI et dans le navigateur).
function _addDaysToFr(iso, days) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return null;
  const n = Number(days);
  if (!Number.isFinite(n)) return null;
  const d = new Date(
    Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + Math.trunc(n)),
  );
  return `${_pad2(d.getUTCDate())}/${_pad2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

// Montant affiché : entier sans décimale (comme le jeu d'exemple « 840 € »),
// sinon 2 décimales. Renvoie null si la saisie n'est pas un nombre fini.
function _euroLabel(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// ── Drapeaux du catalogue B2 (miroir STATIQUE) ───────────────────────────────
// Miroir des entrées kind:'flag' de DIRECT_VAR_CATALOG (lib/direct-vars-catalog.js).
// On ne les importe pas depuis lib/ (ModuleScopePlugin CRA) — le test de parité
// compare cette liste au catalogue serveur et CASSE si un drapeau est ajouté
// côté serveur sans être câblé ici.
export const DIRECT_PREVIEW_FLAG_NAMES = [
  "formulaire_manquant",
  "formulaire_rempli",
  "caution_manquante",
  "caution_recue",
  "caution_empreinte",
  "caution_debit",
  "caution_deposee",
  "animaux_non",
  "fumeur_non",
  "motif_affiche",
  "remboursement_effectue",
  "aucun_remboursement",
  "remboursement_total",
  "remboursement_partiel",
  "remboursement_aucun",
  // Trichotomie CAUTION RESTITUÉE (#21, arrivée dans main par la PR #1089).
  "liberation_totale",
  "prelevement_partiel",
  "prelevement_total",
  // Instructions d'arrivée : envoyées DANS LA FOULÉE (dossier complété à moins
  // d'un jour de l'arrivée) ou À VENIR la veille. Complémentaires — cf.
  // buildDirectPreviewVars, qui en pose toujours exactement UN.
  "instructions_immediates",
  "instructions_a_venir",
  // Motif d'annulation HÔTE (contrat v2 art. 11.2 / 11.1 / 12) — mutuellement
  // exclusifs, posés par le panneau ci-dessous.
  "annulation_notre_fait",
  "annulation_fait_voyageur",
  "annulation_force_majeure",
  // Depuis le PLANCHER TAXE (Dimitri 2026-08-03) : une annulation « non
  // remboursable » rend EXACTEMENT la taxe — ce n'est pas un remboursement
  // du séjour, et le texte doit pouvoir le dire.
  "taxe_seule_remboursee",
];

// ── Modèle de scénario ───────────────────────────────────────────────────────
//
// État NOMINAL : le cas le plus courant d'un séjour qui se passe bien
// (formulaire rempli, caution en empreinte autorisée, séjour court, aucune
// annulation, règles du logement permissives, aucun motif saisi).
//
// `montantSejour` par défaut = 840 : c'est le montant du jeu d'exemple admin
// (JOURNEY_PREVIEW_VARS, « Montant du séjour : 840 € ») — champ éditable dans le
// panneau, sert de comparateur au montant remboursé.
export const DEFAULT_DIRECT_SCENARIO = Object.freeze({
  // Annulation — motif hôte ('' = annulation du voyageur)
  cancelMotif: "",
  // Instructions d'arrivée — 'veille' (cas courant) | 'immediat'
  instructionsTiming: "veille",
  // Formulaire de bienvenue
  formDone: true,
  // Caution — MODE (dérivé de la durée, jamais posé directement)
  stayLength: "court", // 'court' | 'long'
  // Caution — ÉTAT (statut Stripe représentatif, jamais un booléen libre)
  cautionStatus: "authorized", // '' | 'authorized' | 'captured'
  // Annulation & remboursement
  montantRembourse: "",
  // Taxe de séjour incluse dans le total — plancher de remboursement.
  montantTaxe: "",
  // Le remboursement se RÉDUIT-il au plancher taxe ? (≠ montant qui tombe
  // dessus par coïncidence)
  plancherTaxeApplique: false,
  montantSejour: 840,
  // Caution restituée (#21) — MONTANT PRÉLEVÉ sur la caution. Vide par DÉFAUT :
  // exactement comme le serveur, une donnée absente ⇒ AUCUN des 3 drapeaux posé
  // (on ne devine JAMAIS une « libération totale » sur une caution inconnue).
  montantPreleve: "",
  motifPrelevement: "",
  // Règles du logement
  allowsPets: true,
  allowsSmoking: true,
  // Aperçu du MOTIF (exigence B5)
  motifRejet: "",
  motifAnnulation: "",
  motifAnnulationPublic: false,
});

/**
 * Nombre de nuits SIMULÉ pour un scénario. Le choix « court » est calé sur la
 * BORNE EXACTE du seuil (N nuits → empreinte), « long » sur N+1 (→ débit). Le
 * seuil n'est JAMAIS écrit en dur : il vient de CAUTION_HOLD_MAX_NIGHTS.
 * @param {object} scenario
 * @returns {number}
 */
export function scenarioNbNuits(scenario) {
  const s = scenario || {};
  return s.stayLength === "long"
    ? CAUTION_HOLD_MAX_NIGHTS + 1
    : CAUTION_HOLD_MAX_NIGHTS;
}

/**
 * Date de DÉPART cohérente avec le nombre de nuits simulé (arrivée = ancre fixe
 * du jeu d'exemple serveur). C'est ce qui rend l'aperçu arithmétiquement
 * possible : `to − from === nb_nuits`, toujours.
 * @param {object} scenario
 * @returns {string} 'JJ/MM/AAAA'
 */
export function scenarioDepartFr(scenario) {
  return (
    _addDaysToFr(PREVIEW_EXAMPLE_ARRIVEE_ISO, scenarioNbNuits(scenario)) ||
    PREVIEW_EXAMPLE_DEPART_FR
  );
}

// ── Descripteur DÉCLARATIF des contrôles (l'UI est data-driven) ──────────────
//
// kind : 'bool' (case à cocher) | 'choice' (segmented) | 'number' | 'text'.
// `affects` documente les drapeaux/tokens impactés (sert la revue et les tests).
export const DIRECT_SCENARIO_CONTROLS = [
  {
    id: "cancelMotif",
    group: "Annulation — motif",
    kind: "choice",
    label: "Motif de l'annulation (côté hôte)",
    help:
      "Le contrat distingue trois situations aux conséquences opposées : de " +
      "notre fait (remboursement intégral), du fait du voyageur (politique du " +
      "logement), force majeure (prorata).",
    options: [
      { value: "", label: "Annulation du voyageur (aucun motif hôte)" },
      { value: "host_fault", label: "De notre fait" },
      { value: "guest_fault", label: "Du fait du voyageur" },
      { value: "force_majeure", label: "Force majeure" },
    ],
    affects: [
      "annulation_notre_fait",
      "annulation_fait_voyageur",
      "annulation_force_majeure",
    ],
  },
  {
    id: "instructionsTiming",
    group: "Instructions d'arrivée",
    kind: "choice",
    label: "Moment d'envoi des instructions",
    help:
      "Le dossier complété à moins d'un jour de l'arrivée déclenche un envoi " +
      "IMMÉDIAT ; sinon les instructions partent la veille au soir.",
    options: [
      { value: "veille", label: "La veille au soir (cas courant)" },
      { value: "immediat", label: "Dans la foulée (arrivée aujourd'hui ou demain)" },
    ],
    affects: ["instructions_immediates", "instructions_a_venir"],
  },
  {
    id: "formDone",
    group: "Formulaire de bienvenue",
    kind: "bool",
    label: "Formulaire de bienvenue rempli",
    affects: ["formulaire_rempli", "formulaire_manquant"],
  },
  {
    id: "stayLength",
    group: "Caution — mode",
    kind: "choice",
    label: "Durée du séjour",
    help: `Le mode de caution découle de la durée (seuil : ${CAUTION_HOLD_MAX_NIGHTS} nuits) — il ne se choisit pas directement.`,
    options: [
      {
        value: "court",
        label: `Séjour court (≤ ${CAUTION_HOLD_MAX_NIGHTS} nuits) → empreinte`,
      },
      {
        value: "long",
        label: `Séjour long (> ${CAUTION_HOLD_MAX_NIGHTS} nuits) → débit`,
      },
    ],
    affects: ["caution_empreinte", "caution_debit", "nb_nuits"],
  },
  {
    id: "cautionStatus",
    group: "Caution — état",
    kind: "choice",
    label: "État de la caution",
    help: "L'état vient du statut réel côté Stripe — jamais d'un simple oui/non.",
    options: [
      { value: "", label: "Non reçue" },
      { value: "authorized", label: "Reçue (empreinte autorisée)" },
      { value: "captured", label: "Reçue (débitée)" },
    ],
    affects: ["caution_recue", "caution_manquante", "caution_deposee"],
  },
  {
    // ⚠️ CHEMIN ARGENT — même règle que le mode : AUCUNE case ne pose
    // directement `liberation_totale` / `prelevement_*`. On saisit le MONTANT
    // PRÉLEVÉ (P) ; la trichotomie est DÉRIVÉE de P vs le montant de caution (C)
    // du jeu d'exemple, exactement comme le serveur. Impossible, donc, de
    // valider un texte « caution intégralement libérée » sur un prélèvement.
    id: "montantPreleve",
    group: "Caution — restitution",
    kind: "number",
    label: "Montant prélevé sur la caution (€)",
    placeholder: "aucun prélèvement saisi",
    help: "Comparé au montant de la caution : 0 → libération totale, entre les deux → prélèvement partiel, au-delà → prélèvement total.",
    affects: [
      "liberation_totale",
      "prelevement_partiel",
      "prelevement_total",
      "montant_preleve",
      "montant_libere",
    ],
  },
  {
    id: "motifPrelevement",
    group: "Caution — restitution",
    kind: "text",
    label: "Motif du prélèvement",
    placeholder: "Remplacement d'un drap taché.",
    affects: ["motif_prelevement"],
  },
  {
    id: "montantTaxe",
    group: "Annulation & remboursement",
    kind: "number",
    label: "Taxe de séjour incluse (€)",
    placeholder: "aucune taxe",
    help:
      "La taxe est remboursée quelle que soit la politique (contrat art. 11). " +
      "Un remboursement ÉGAL à ce montant = « seule la taxe est remboursée ».",
    affects: ["montant_taxe"],
  },
  {
    id: "plancherTaxeApplique",
    group: "Annulation & remboursement",
    kind: "bool",
    label: "Seule la taxe est remboursée (plancher appliqué)",
    help:
      "Coché = le séjour n'est pas remboursé et seule la taxe revient au " +
      "voyageur. Un montant qui vaut la taxe par coïncidence n'est PAS ce cas.",
    affects: ["taxe_seule_remboursee", "aucun_remboursement", "remboursement_aucun"],
  },
  {
    id: "montantRembourse",
    group: "Annulation & remboursement",
    kind: "number",
    label: "Montant remboursé (€)",
    placeholder: "aucun remboursement saisi",
    affects: [
      "remboursement_effectue",
      "remboursement_total",
      "remboursement_partiel",
      "aucun_remboursement",
      "remboursement_aucun",
      "montant_rembourse",
    ],
  },
  {
    id: "montantSejour",
    group: "Annulation & remboursement",
    kind: "number",
    label: "Montant du séjour (€)",
    affects: ["remboursement_total", "remboursement_partiel", "montant_sejour"],
  },
  {
    id: "allowsPets",
    group: "Règles du logement",
    kind: "bool",
    label: "Animaux autorisés",
    affects: ["animaux_non"],
  },
  {
    id: "allowsSmoking",
    group: "Règles du logement",
    kind: "bool",
    label: "Fumeur autorisé",
    affects: ["fumeur_non"],
  },
  {
    id: "motifRejet",
    group: "Aperçu du motif",
    kind: "text",
    label: "Motif de refus",
    placeholder: "Ces dates ne sont malheureusement plus disponibles.",
    affects: ["motif_rejet", "motifLine"],
  },
  {
    id: "motifAnnulation",
    group: "Aperçu du motif",
    kind: "text",
    label: "Motif d'annulation",
    placeholder: "Un imprévu nous oblige à annuler ce séjour.",
    affects: ["motif_annulation", "motif_affiche"],
  },
  {
    id: "motifAnnulationPublic",
    group: "Aperçu du motif",
    kind: "bool",
    label: "Afficher ce motif dans l'email au voyageur",
    affects: ["motif_affiche"],
  },
];

// Ordre d'affichage des groupes (dérivé du descripteur, sans doublon).
export const DIRECT_SCENARIO_GROUPS = DIRECT_SCENARIO_CONTROLS.reduce(
  (acc, c) => (acc.includes(c.group) ? acc : acc.concat(c.group)),
  [],
);

// ── Scénario → signaux serveur ───────────────────────────────────────────────
//
// Traduit l'état du panneau dans le VOCABULAIRE EXACT des `signals` attendus par
// deriveDirectConditionFlags (serveur). C'est le seul point de traduction :
// au-delà, la dérivation est byte-parallèle au serveur.
export function scenarioSignals(scenario) {
  const s = { ...DEFAULT_DIRECT_SCENARIO, ...(scenario || {}) };
  return {
    formDone: s.formDone === true,
    // '' (non reçue) est une VALEUR, pas une absence → toujours défini, donc les
    // 3 drapeaux caution sont toujours dérivés (jamais un oubli silencieux).
    cautionStatus: s.cautionStatus == null ? "" : String(s.cautionStatus),
    allowsPets: s.allowsPets !== false,
    allowsSmoking: s.allowsSmoking !== false,
    cancellationReasonPublic: s.motifAnnulationPublic === true,
    montantTaxe: _isNonEmpty(s.montantTaxe) ? s.montantTaxe : null,
    // Le plancher s'applique-t-il RÉELLEMENT ? (case du panneau, jamais déduit)
    remboursementPlancherTaxe: s.plancherTaxeApplique === true,
    montantRembourse: _isNonEmpty(s.montantRembourse)
      ? s.montantRembourse
      : null,
    montantSejour: _isNonEmpty(s.montantSejour) ? s.montantSejour : null,
    // Caution restituée (#21) : `null` quand la saisie est vide → le serveur
    // (comme le client) ne pose AUCUN des 3 drapeaux. Ne jamais coercer en 0 :
    // 0 signifierait « rien prélevé » (liberation_totale), ce qui est un FAIT,
    // pas une absence de saisie.
    montantPreleve: _isNonEmpty(s.montantPreleve) ? s.montantPreleve : null,
  };
}

// ── Scénario → tokens de contexte ────────────────────────────────────────────
//
// Ne pose QUE ce que le scénario pilote réellement. Une saisie vide LAISSE la
// valeur d'exemple du serveur (on n'efface jamais un exemple par une saisie
// vide) — sauf nb_nuits ET LES DATES DU SÉJOUR, entièrement pilotés par le choix
// de durée (sinon l'aperçu affiche une combinaison impossible, cf. en-tête).
export function scenarioTokens(scenario) {
  const s = { ...DEFAULT_DIRECT_SCENARIO, ...(scenario || {}) };
  const nbNuits = scenarioNbNuits(s);
  const arrivee = PREVIEW_EXAMPLE_ARRIVEE_FR;
  const depart = scenarioDepartFr(s);
  const out = {
    nbNuits,
    nb_nuits: nbNuits,
    // ARRIVÉE = ancre (re-posée explicitement : l'invariant doit être lisible
    // dans le contexte d'aperçu, pas seulement hérité du jeu d'exemple).
    from: arrivee,
    startDate: arrivee,
    date_arrivee: arrivee,
    checkin_date: arrivee,
    // DÉPART = arrivée + nbNuits → `to − from === nb_nuits`, TOUJOURS.
    to: depart,
    endDate: depart,
    date_depart: depart,
    checkout_date: depart,
    dates: `${arrivee} au ${depart}`,
  };

  if (_isNonEmpty(s.motifRejet)) {
    out.motif_rejet = String(s.motifRejet);
    // Le builder `rejected_voyageur` utilise le camelCase historique {motifLine}
    // (dont motif_rejet n'est que l'alias FR) → on pose LES DEUX.
    out.motifLine = String(s.motifRejet);
  }
  if (_isNonEmpty(s.motifAnnulation)) {
    out.motif_annulation = String(s.motifAnnulation);
  }
  if (_isNonEmpty(s.montantRembourse)) {
    out.montant_rembourse = s.montantRembourse;
  }
  if (_isNonEmpty(s.montantSejour)) {
    out.montant_sejour = s.montantSejour;
  }
  // Caution restituée (#21) : {montant_preleve} est un nombre NU (le gabarit
  // écrit « X € »). {montant_libere} = C − P n'est PAS posé ici : C (le montant
  // de caution) appartient au jeu d'exemple, pas au scénario — il est calculé
  // dans buildDirectPreviewVars, où les deux sont connus.
  if (_isNonEmpty(s.montantPreleve)) {
    out.montant_preleve = s.montantPreleve;
  }
  if (_isNonEmpty(s.motifPrelevement)) {
    out.motif_prelevement = String(s.motifPrelevement);
  }
  return out;
}

/**
 * Réaligne les FRAGMENTS COMPOSÉS du jeu d'exemple serveur sur le scénario.
 *
 * Certaines valeurs d'exemple sont des PHRASES déjà assemblées côté serveur —
 * `montantLine` (« Montant du séjour : 840 € »), `cancelOriginLine`
 * (« Votre réservation à Maison Zen (12/07/2026 au 19/07/2026) a été annulée. »)…
 * Poser `to` ne les corrige pas : elles continueraient d'afficher la date/le
 * montant d'origine, à côté d'un `{nbNuits}` piloté par le panneau.
 *
 * On ne RECOMPOSE pas ces phrases (ce serait dupliquer la formulation serveur) :
 * on y remplace la date de départ et le montant du jeu d'exemple par les valeurs
 * du scénario. Substitution NEUTRE quand le scénario vaut l'exemple.
 * Les deux motifs remplacés sont non ambigus : « 19/07/2026 » est la seule date
 * postérieure à l'arrivée du jeu d'exemple, et « 840 € » n'y désigne que le
 * montant du séjour (la caution s'écrit « 300 € »).
 *
 * @param {object} vars contexte d'aperçu (jeu d'exemple + tokens du scénario)
 * @param {object} scenario état du panneau
 * @returns {object} nouveau contexte (jamais muté sur place)
 */
export function realignExampleStrings(vars, scenario) {
  const s = { ...DEFAULT_DIRECT_SCENARIO, ...(scenario || {}) };
  const depart = scenarioDepartFr(s);
  const montant = _isNonEmpty(s.montantSejour)
    ? _euroLabel(s.montantSejour)
    : null;
  const subs = [];
  if (depart && depart !== PREVIEW_EXAMPLE_DEPART_FR) {
    subs.push([PREVIEW_EXAMPLE_DEPART_FR, depart]);
  }
  if (montant != null && montant !== String(PREVIEW_EXAMPLE_MONTANT_SEJOUR)) {
    subs.push([`${PREVIEW_EXAMPLE_MONTANT_SEJOUR} €`, `${montant} €`]);
  }
  if (subs.length === 0) return { ...(vars || {}) };

  const rewrite = (str) =>
    subs.reduce((acc, [from, to]) => acc.split(from).join(to), str);
  const out = {};
  for (const [k, v] of Object.entries(vars || {})) {
    if (typeof v === "string") out[k] = rewrite(v);
    // `promoBlock` & co. sont des TABLEAUX de lignes : même traitement.
    else if (Array.isArray(v))
      out[k] = v.map((x) => (typeof x === "string" ? rewrite(x) : x));
    else out[k] = v;
  }
  return out;
}

// ── Groupes de contrôles pertinents pour un texte donné ──────────────────────
//
// Sert l'ergonomie du panneau (B5 / QA #1095) : les groupes sont REPLIÉS par
// défaut et seuls ceux dont un drapeau est RÉELLEMENT utilisé par le gabarit
// affiché s'ouvrent. Un gabarit sans `{#if}` → panneau entièrement replié, donc
// aperçu visible sans scroller (exigence produit « même écran »).
const _IF_OPEN_RE = /\{#if\s+([a-zA-Z_][a-zA-Z0-9_ \t]*)\}/g;
const _IF_CUMUL_OPERATORS = new Set(["ET", "OU"]);

/**
 * Noms de drapeaux référencés par les `{#if …}` de textes donnés (opérandes du
 * cumul ET/OU inclus, jetons ET/OU exclus). Pur, fail-doux.
 * @param {Array<string>} texts
 * @returns {string[]}
 */
export function conditionNamesInTexts(texts) {
  const out = [];
  const seen = new Set();
  for (const t of Array.isArray(texts) ? texts : [texts]) {
    if (typeof t !== "string" || t === "") continue;
    _IF_OPEN_RE.lastIndex = 0;
    let m;
    while ((m = _IF_OPEN_RE.exec(t)) !== null) {
      for (const op of String(m[1]).split(/\s+/)) {
        if (!op || _IF_CUMUL_OPERATORS.has(op) || seen.has(op)) continue;
        seen.add(op);
        out.push(op);
      }
    }
  }
  return out;
}

/**
 * Groupes du panneau à OUVRIR pour un gabarit : ceux dont au moins un contrôle
 * pilote un drapeau/token utilisé par le texte.
 * @param {Array<string>} texts objet + corps du gabarit affiché
 * @returns {string[]} sous-ensemble de DIRECT_SCENARIO_GROUPS (ordre d'affichage)
 */
export function scenarioGroupsForTexts(texts) {
  const used = new Set(conditionNamesInTexts(texts));
  if (used.size === 0) return [];
  const groups = new Set();
  for (const ctl of DIRECT_SCENARIO_CONTROLS) {
    if ((ctl.affects || []).some((a) => used.has(a))) groups.add(ctl.group);
  }
  return DIRECT_SCENARIO_GROUPS.filter((g) => groups.has(g));
}

// La taxe est-elle un MONTANT exploitable ? (miroir client/serveur)
function _taxePositive(v) {
  if (v == null || String(v).trim() === "") return false;
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
}


/**
 * Drapeaux {#if …} d'un contexte d'aperçu — MIROIR EXACT de
 * `deriveDirectConditionFlags(vars, signals)` (lib/direct-vars-catalog.js).
 * Même signature, même ordre, même fail-doux (clé ABSENTE quand le signal est
 * inconnu → bloc masqué).
 *
 * @param {object} vars contexte déjà fusionné (porte nbNuits, motif_annulation…)
 * @param {object} signals signaux bruts (voir scenarioSignals)
 * @returns {object} drapeaux ("1" = vrai, "" = faux, absent = inconnu)
 */
export function deriveDirectPreviewFlags(vars = {}, signals = {}) {
  const v = vars || {};
  const sg = signals || {};
  const out = {};
  const TRUE = "1";

  // Formulaire de bienvenue.
  if (sg.formDone === true) out.formulaire_rempli = TRUE;
  else if (sg.formDone === false) out.formulaire_manquant = TRUE;

  // CAUTION — mode : SELON LE NOMBRE DE NUITS (jamais le statut Stripe).
  const nbNuits = Number(v.nbNuits != null ? v.nbNuits : v.nb_nuits);
  const mode = cautionModeForNights(nbNuits);
  out.caution_empreinte = mode === "empreinte" ? TRUE : "";
  out.caution_debit = mode === "debit" ? TRUE : "";

  // CAUTION — état : fonction canonique cautionRecue() sur les 9 statuts.
  if (sg.cautionStatus !== undefined) {
    const recue = cautionRecue({
      source: "direct",
      caution_status: sg.cautionStatus,
    });
    out.caution_recue = recue ? TRUE : "";
    out.caution_manquante = recue ? "" : TRUE;
    out.caution_deposee = recue ? TRUE : "";
  }

  // CAUTION — restitution (#21) : trichotomie P vs C. C = montant de caution du
  // contexte (alias caution_montant / cautionAmount), P = signals.montantPreleve.
  // Fail-doux : C ou P absent ⇒ aucune des 3 posée. ORDRE D'INSERTION identique
  // au serveur (le test de parité compare les objets sérialisés).
  const cautionC =
    v.caution_montant != null ? v.caution_montant : v.cautionAmount;
  Object.assign(
    out,
    deriveCautionRestitutionFlags(cautionC, sg.montantPreleve),
  );

  // Règles logement (négations : « non autorisé »).
  if (sg.allowsPets === false) out.animaux_non = TRUE;
  if (sg.allowsSmoking === false) out.fumeur_non = TRUE;

  // Motif d'annulation AFFICHÉ = motif présent ∧ case publique cochée (#1066).
  if (_isNonEmpty(v.motif_annulation) && sg.cancellationReasonPublic === true) {
    out.motif_affiche = TRUE;
  }

  // Remboursement (MANUEL, saisi admin — pas de barème).
  const rembRaw = sg.montantRembourse;
  if (rembRaw != null && String(rembRaw).trim() !== "") {
    const remb = Number(rembRaw);
    // 🔴 TAXE SEULE — MIROIR EXACT du serveur (signal explicite, jamais une
    // égalité de montants : un remboursement qui TOMBE sur la taxe par
    // coïncidence n'est pas un plancher). Parité vérifiée par test exhaustif.
    // ⚠️ `taxe > 0` EXIGÉ (contre-revue #1170) : le drapeau pouvait s'armer
    // sans montant — la phrase « seule la taxe de séjour vous est remboursée »
    // partait alors avec un montant VIDE. L'ancienne égalité le garantissait
    // implicitement ; le signal explicite doit le dire.
    if (sg.remboursementPlancherTaxe === true && _taxePositive(sg.montantTaxe)) {
      const taxeRaw = sg.montantTaxe;
      const taxe =
        taxeRaw != null && String(taxeRaw).trim() !== "" ? Number(taxeRaw) : NaN;
      out.aucun_remboursement = TRUE;
      out.remboursement_aucun = TRUE;
      out.taxe_seule_remboursee = TRUE;
      if (Number.isFinite(taxe) && taxe > 0) out.montant_taxe = String(taxe);
      return out;
    }
    if (Number.isFinite(remb)) {
      if (remb > 0) {
        out.remboursement_effectue = TRUE;
        const sejRaw = sg.montantSejour;
        const sej =
          sejRaw != null && String(sejRaw).trim() !== "" ? Number(sejRaw) : NaN;
        if (Number.isFinite(sej) && sej > 0) {
          if (remb >= sej) out.remboursement_total = TRUE;
          else out.remboursement_partiel = TRUE;
        }
      } else {
        out.aucun_remboursement = TRUE;
        out.remboursement_aucun = TRUE;
      }
    }
  }

  return out;
}

/**
 * Contexte d'aperçu COMPLET = variables d'exemple (serveur) + tokens du scénario
 * + drapeaux dérivés.
 *
 * INVARIANT DE SÛRETÉ : les drapeaux du catalogue sont TOUS définis ("1" ou "")
 * — jamais `undefined`. Un drapeau absent donnerait le même résultat visuel
 * (résolveur → null → bloc masqué), mais on ne veut pas dépendre d'un oubli.
 *
 * FAIL-SAFE : ne jette JAMAIS (un aperçu qui crashe fermerait l'écran d'édition).
 *
 * @param {object} exampleVars variables d'exemple servies par le serveur
 * @param {object} scenario état du panneau de scénario
 * @returns {object}
 */
export function buildDirectPreviewVars(exampleVars, scenario) {
  try {
    const s = { ...DEFAULT_DIRECT_SCENARIO, ...(scenario || {}) };
    // 1) jeu d'exemple serveur, 2) fragments composés réalignés sur le scénario
    //    (dates + montant), 3) tokens pilotés par le panneau (qui font foi).
    const base = realignExampleStrings(exampleVars || {}, s);
    const merged = { ...base, ...scenarioTokens(s) };
    // {montant_libere} = caution − prélevé (miroir de buildDirectComputedVars,
    // qui le calcule au call-site serveur). Il croise le jeu d'exemple (C) et le
    // panneau (P) → il ne peut être posé qu'ICI. Jamais négatif ; jamais posé si
    // l'un des deux manque (aperçu muet plutôt que faux sur le chemin argent).
    const cRaw =
      merged.caution_montant != null
        ? merged.caution_montant
        : merged.cautionAmount;
    if (_isNonEmpty(cRaw) && _isNonEmpty(s.montantPreleve)) {
      const C = Number(cRaw);
      const P = Number(s.montantPreleve);
      if (Number.isFinite(C) && Number.isFinite(P)) {
        merged.montant_libere = Math.max(0, C - P);
      }
    }
    const flags = deriveDirectPreviewFlags(merged, scenarioSignals(s));
    const out = { ...merged, ...flags };
    // {instructions_a_venir} = COMPLÉMENT de {instructions_immediates} — MIROIR
    // EXACT d'enrichDirectVars (lib/direct-vars-catalog.js). Le moteur ne connaît
    // pas la négation : sans ce complément, les DEUX blocs du gabarit « séjour
    // confirmé » seraient masqués et l'aperçu montrerait une carte SANS PHRASE.
    // L'aperçu n'a pas de contrôle dédié : il décrit le cas NOMINAL (instructions
    // la veille), qui est aussi le comportement par défaut du parcours.
    // Le panneau fait foi (contrôle `instructionsTiming`) ; à défaut, on
    // retombe sur le drapeau éventuellement porté par le jeu d'exemple.
    const immediat =
      s.instructionsTiming === "immediat" || _isNonEmpty(out.instructions_immediates);
    // Motif d'annulation : au plus UN drapeau, jamais deux (miroir exact de
    // lib/booking-cancel-handlers.js — un texte qui afficherait deux motifs
    // contradictoires serait pire qu'un texte muet).
    out.annulation_notre_fait = s.cancelMotif === "host_fault" ? "1" : "";
    out.annulation_fait_voyageur = s.cancelMotif === "guest_fault" ? "1" : "";
    out.annulation_force_majeure = s.cancelMotif === "force_majeure" ? "1" : "";
    out.instructions_immediates = immediat ? "1" : "";
    out.instructions_a_venir = immediat ? "" : "1";
    for (const name of DIRECT_PREVIEW_FLAG_NAMES) {
      if (out[name] == null) out[name] = "";
    }
    return out;
  } catch (_) {
    return { ...(exampleVars || {}) };
  }
}
